import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Inbox, ListChecks } from 'lucide-react';
import {
  Card, CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { MetricSkeleton } from '@/components/dashboard/MetricSkeleton';
import { RiskBar } from '@/components/dashboard/RiskBar';
import { SeverityChip } from '@/components/dashboard/SeverityChip';
import { WidgetCardHeader } from '@/components/dashboard/WidgetCardHeader';
import DSRDetailPanel from '@/components/dashboard/DSRDetailPanel';
import { useDsrPanel } from '@/components/dashboard/useDsrPanel';
import {
  TIME_PERIOD_TODAY,
  TIME_PERIOD_7D,
  type TimePeriod,
} from '@/components/dashboard/timePeriod';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/services/db/supabase';
import {
  getRiskDistributionToday,
  getPortfolioRiskDistribution,
  getSafetyEventsAnalysis,
  getSWAEventsAnalysis,
  getProjectsWithNoDSRToday,
  type RiskDistribution,
  type SafetyEventsAnalysis,
  type SWAEventsAnalysis,
  type ProjectNoActivity,
} from '@/services/db/dashboard';
import {
  getDSRTrend,
  getSafetyEventTrend as getSafetyEventDailyTrend,
  getSSETrend,
  getTopActionItems,
  getProjectIdsForSupervisor,
} from '@/services/db/analytics';
import { getSSEByProject, type SSEByProjectRow } from '@/services/db/pob';
import { getActiveAlertsBreakdown, type ActiveAlertsBreakdown } from '@/services/db/alerts';
import type { AlertSeverity } from '@/types/alerts';
import { ALERT_TYPE_LABEL } from '@/types/alerts';
import {
  ChartCard,
  DailyRiskChart,
  SafetyEventLineChart,
  SSEAreaChart,
  TopActionItemsCard,
  useChartState,
} from '@/components/analytics/AnalyticsCharts';

const STALE = 5 * 60 * 1000;
const today = () => format(new Date(), 'yyyy-MM-dd');

// ── Hooks ───────────────────────────────────────────────────

function useTenantName() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['tenant_name', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('name')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      return data?.name ?? 'Your Scope';
    },
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useScopeProjectIds() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const userId = useAuthStore((s) => s.user?.id);
  return useQuery({
    queryKey: ['supv_scope_project_ids', tenantId, userId],
    queryFn: () => getProjectIdsForSupervisor(userId!, tenantId!),
    enabled: !!tenantId && !!userId,
    staleTime: STALE,
  });
}

interface DSRsTodayBreakdown {
  submitted: number;
  draft: number;
  total: number;
  noActivity: ProjectNoActivity[];
  hasHigh: boolean;
}

function useDSRsToday(projectIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['supv_dsrs_today', projectIds, today()],
    queryFn: async (): Promise<DSRsTodayBreakdown> => {
      if (projectIds.length === 0) {
        return { submitted: 0, draft: 0, total: 0, noActivity: [], hasHigh: false };
      }
      const reportDate = today();
      const [statusRes, noActivityRes] = await Promise.all([
        supabase
          .from('dsr_reports')
          .select(
            'status, dsr_activities_summaries(overall_risk_level)',
          )
          .eq('is_deleted', false)
          .eq('report_date', reportDate)
          .in('project_id', projectIds),
        getProjectsWithNoDSRToday(projectIds, reportDate),
      ]);
      if (statusRes.error) throw statusRes.error;
      const rows = (statusRes.data ?? []) as Array<{
        status: string;
        dsr_activities_summaries: { overall_risk_level: string | null }[] | null;
      }>;
      const submitted = rows.filter((r) => r.status === 'submitted').length;
      const draft = rows.filter((r) => r.status === 'draft').length;
      const hasHigh = rows.some((r) => {
        const lvl = (r.dsr_activities_summaries ?? [])[0]?.overall_risk_level;
        return lvl === 'high' || lvl === 'critical';
      });
      return {
        submitted,
        draft,
        total: rows.length,
        noActivity: noActivityRes,
        hasHigh,
      };
    },
    enabled,
    staleTime: STALE,
  });
}

// Supervisor risk-distribution widget defaults to "Today" but allows
// historical backfill (7d/30d/90d/custom) per AC 12.1.10. When the user
// picks a window, we swap from `getRiskDistributionToday` to the windowed
// `getPortfolioRiskDistribution` — both return the same RiskDistribution
// shape. This keeps the today-only optimisation intact for the common
// case while honouring the per-widget time selector.
function useRiskDistribution(
  projectIds: string[],
  enabled: boolean,
  period: TimePeriod,
) {
  return useQuery({
    queryKey: ['supv_risk_dist', projectIds, period.days, period.key, today()],
    queryFn: () =>
      period.key === 'today'
        ? getRiskDistributionToday(projectIds, today())
        : getPortfolioRiskDistribution(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function useActiveAlerts(projectIds: string[], enabled: boolean) {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['supv_active_alerts', tenantId, projectIds],
    queryFn: () =>
      getActiveAlertsBreakdown(tenantId!, { projectIds, recentLimit: 5 }),
    enabled: enabled && !!tenantId,
    staleTime: STALE,
  });
}

function useSSEToday(projectIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['supv_sse_today', projectIds, today()],
    queryFn: () => getSSEByProject(projectIds, today()),
    enabled,
    staleTime: STALE,
  });
}

function useSafetyEvents(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['supv_safety', projectIds, period.days],
    queryFn: () => getSafetyEventsAnalysis(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function useSWAEvents(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['supv_swa', projectIds, period.days],
    queryFn: () => getSWAEventsAnalysis(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

interface RecentDSRRow {
  id: string;
  reportDate: string;
  shiftType: string;
  status: string;
  submittedAt: string | null;
  projectId: string;
  projectName: string;
  locationName: string | null;
  ssrName: string | null;
  overallRiskLevel: string | null;
  activityCount: number;
  swaCount: number;
  safetyEventCount: number;
  positiveObservationCount: number;
}

function useRecentDSRs(projectIds: string[], enabled: boolean, limit: number) {
  return useQuery({
    queryKey: ['supv_recent_dsrs', projectIds, limit],
    queryFn: async (): Promise<RecentDSRRow[]> => {
      if (projectIds.length === 0) return [];
      const { data, error } = await supabase
        .from('dsr_reports')
        .select(`
          id, report_date, shift_type, status, submitted_at, project_id,
          projects!inner(name),
          users:created_by(first_name, last_name),
          locations:location_id(name),
          dsr_activities_summaries(overall_risk_level),
          dsr_activities(id),
          dsr_swa_events(id),
          dsr_safety_events(id),
          dsr_hse_observations(id, observation_type)
        `)
        .eq('is_deleted', false)
        .eq('status', 'submitted')
        .in('project_id', projectIds)
        .order('submitted_at', { ascending: false, nullsFirst: false })
        .order('report_date', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return ((data ?? []) as any[]).map((row) => {
        const observations = (row.dsr_hse_observations ?? []) as Array<{
          id: string;
          observation_type: string;
        }>;
        return {
          id: row.id,
          reportDate: row.report_date,
          shiftType: row.shift_type,
          status: row.status,
          submittedAt: row.submitted_at,
          projectId: row.project_id,
          projectName: row.projects?.name ?? '—',
          locationName: row.locations?.name ?? null,
          ssrName: row.users
            ? `${row.users.first_name ?? ''} ${row.users.last_name ?? ''}`.trim() || null
            : null,
          overallRiskLevel:
            (row.dsr_activities_summaries ?? [])[0]?.overall_risk_level ?? null,
          activityCount: (row.dsr_activities ?? []).length,
          swaCount: (row.dsr_swa_events ?? []).length,
          safetyEventCount: (row.dsr_safety_events ?? []).length,
          positiveObservationCount: observations.filter(
            (o) => o.observation_type === 'positive',
          ).length,
        };
      });
    },
    enabled,
    staleTime: STALE,
  });
}

function useTrendAnalytics(projectIds: string[], enabled: boolean) {
  const dsrTrend = useQuery({
    queryKey: ['supv_dsr_trend', projectIds],
    queryFn: () => getDSRTrend(projectIds, 30),
    enabled,
    staleTime: STALE,
  });
  const safetyTrend = useQuery({
    queryKey: ['supv_safety_daily', projectIds],
    queryFn: () => getSafetyEventDailyTrend(projectIds, 30),
    enabled,
    staleTime: STALE,
  });
  const sseTrend = useQuery({
    queryKey: ['supv_sse_trend', projectIds],
    queryFn: () => getSSETrend(projectIds, 30),
    enabled,
    staleTime: STALE,
  });
  const actions = useQuery({
    queryKey: ['supv_top_actions', projectIds],
    queryFn: () => getTopActionItems(projectIds, 5),
    enabled,
    staleTime: STALE,
  });
  return { dsrTrend, safetyTrend, sseTrend, actions };
}

// ── Display helpers ─────────────────────────────────────────

function trendIndicator(trend: 'up' | 'down' | 'flat'): { symbol: string; color: string } {
  switch (trend) {
    case 'up':
      return { symbol: '↑', color: 'text-mozaic-red' };
    case 'down':
      return { symbol: '↓', color: 'text-mozaic-green' };
    case 'flat':
      return { symbol: '→', color: 'text-muted-foreground' };
  }
}

function formatCategory(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const RISK_LEVEL_STYLE: Record<string, string> = {
  critical: 'bg-mozaic-red/15 text-mozaic-red',
  high: 'bg-mozaic-red/15 text-mozaic-red',
  medium: 'bg-mozaic-orange/15 text-mozaic-orange',
  low: 'bg-mozaic-green/15 text-mozaic-green',
};

function formatRiskLevel(v: string | null): string {
  if (!v) return '—';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

const ALERT_SEVERITY_ORDER: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];
const ALERT_SEVERITY_STYLE: Record<AlertSeverity, string> = {
  critical: 'bg-mozaic-red/15 text-mozaic-red',
  high: 'bg-mozaic-red/15 text-mozaic-red',
  medium: 'bg-mozaic-orange/15 text-mozaic-orange',
  low: 'bg-muted text-foreground',
};

// SWA severity palette matches the mobile SWA form (teal/blue/orange/red)
// for operator-side consistency. Reconciled in S82b — Supervisor previously
// rendered "minor" as green and "serious" as red, which diverged from both
// Executive and the mobile entry surface.
const SWA_SEVERITY_STYLE: Record<string, string> = {
  critical: 'bg-mozaic-red/15 text-mozaic-red',
  serious: 'bg-mozaic-orange/15 text-mozaic-orange',
  moderate: 'bg-mozaic-blue/15 text-mozaic-blue',
  minor: 'bg-mozaic-teal/15 text-mozaic-teal',
};

// ── Row 1: Active DSRs Today ────────────────────────────────

function ActiveDSRsTodayCard({
  query,
  onProjectClick,
}: {
  query: ReturnType<typeof useDSRsToday>;
  onProjectClick: (projectId: string) => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Active DSRs Today"
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : (
          <ActiveDSRsTodayContent data={query.data} onProjectClick={onProjectClick} />
        )}
      </CardContent>
    </Card>
  );
}

function ActiveDSRsTodayContent({
  data,
  onProjectClick,
}: {
  data: DSRsTodayBreakdown;
  onProjectClick: (projectId: string) => void;
}) {
  const noActivityCount = data.noActivity.length;
  const noActivityColor =
    noActivityCount === 0 ? 'text-mozaic-green' : 'text-mozaic-orange';
  return (
    <>
      <div className="flex items-baseline gap-2">
        <p className="text-3xl font-bold">{data.submitted}</p>
        <span className="text-sm text-muted-foreground">submitted</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">In draft</span>
          <span className={`font-medium ${data.draft > 0 ? 'text-mozaic-orange' : ''}`}>
            {data.draft}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">No DSR yet</span>
          <span className={`font-medium ${noActivityColor}`}>{noActivityCount}</span>
        </div>
      </div>
      {noActivityCount > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">
            Projects with no activity:
          </p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {data.noActivity.slice(0, 5).map((p) => (
              <li key={p.projectId}>
                <button
                  type="button"
                  className="truncate text-left text-mozaic-teal hover:underline"
                  onClick={() => onProjectClick(p.projectId)}
                >
                  {p.projectName}
                </button>
              </li>
            ))}
          </ul>
          {noActivityCount > 5 && (
            <p className="mt-1 text-xs text-muted-foreground">
              +{noActivityCount - 5} more
            </p>
          )}
        </div>
      )}
    </>
  );
}

// ── Row 1: Risk Distribution Today ──────────────────────────

function RiskDistributionCard({
  query,
  period,
  onPeriodChange,
  hasHigh,
  onRiskClick,
}: {
  query: ReturnType<typeof useRiskDistribution>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  hasHigh: boolean;
  onRiskClick: (level: 'high' | 'medium' | 'low' | 'not_assessed') => void;
}) {
  const titleSuffix = period.key === 'today' ? 'Today' : '';
  return (
    <Card className={hasHigh ? 'border-mozaic-orange/60' : undefined}>
      <WidgetCardHeader
        title={`Overall Risk Distribution${titleSuffix ? ' ' + titleSuffix : ''}`}
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
        rightSlot={
          hasHigh ? (
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-mozaic-orange" />
          ) : null
        }
      />
      <CardContent>
        {query.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <p className="pt-2 text-xs text-muted-foreground">
            {period.key === 'today'
              ? 'No DSRs submitted today'
              : 'No submitted DSRs in the selected period'}
          </p>
        ) : (
          <RiskDistributionBars
            data={query.data}
            period={period}
            onRiskClick={onRiskClick}
          />
        )}
      </CardContent>
    </Card>
  );
}

function RiskDistributionBars({
  data,
  period,
  onRiskClick,
}: {
  data: RiskDistribution;
  period: TimePeriod;
  onRiskClick: (level: 'high' | 'medium' | 'low' | 'not_assessed') => void;
}) {
  return (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={() => onRiskClick('high')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="High" pct={data.highPct} count={data.high} barClass="bg-mozaic-red" />
      </button>
      <button
        type="button"
        onClick={() => onRiskClick('medium')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="Medium" pct={data.mediumPct} count={data.medium} barClass="bg-mozaic-orange" />
      </button>
      <button
        type="button"
        onClick={() => onRiskClick('low')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="Low" pct={data.lowPct} count={data.low} barClass="bg-mozaic-green" />
      </button>
      {data.notAssessed > 0 && (
        <button
          type="button"
          onClick={() => onRiskClick('not_assessed')}
          className="block w-full text-left hover:opacity-80"
        >
          <RiskBar
            label="Not Assessed"
            pct={data.notAssessedPct}
            count={data.notAssessed}
            barClass="bg-muted-foreground/50"
          />
        </button>
      )}
      <p className="pt-1 text-xs text-muted-foreground">
        {data.total} DSR {data.total === 1 ? 'day' : 'days'}
        {period.key === 'today' ? ' today' : ` in last ${period.days}d`}
      </p>
    </div>
  );
}

// ── Row 1: Active Alerts ────────────────────────────────────

function ActiveAlertsCard({
  query,
  projectNames,
  onViewAll,
  onSeverityClick,
}: {
  query: ReturnType<typeof useActiveAlerts>;
  projectNames: Map<string, string>;
  onViewAll: () => void;
  onSeverityClick: (sev: AlertSeverity) => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Active Alerts"
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <div className="flex flex-col items-start gap-1.5">
            <p className="text-3xl font-bold text-mozaic-green">0</p>
            <p className="text-xs text-muted-foreground">
              No active alerts in scope — everything is acknowledged.
            </p>
          </div>
        ) : (
          <ActiveAlertsContent
            data={query.data}
            projectNames={projectNames}
            onViewAll={onViewAll}
            onSeverityClick={onSeverityClick}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ActiveAlertsContent({
  data,
  projectNames,
  onViewAll,
  onSeverityClick,
}: {
  data: ActiveAlertsBreakdown;
  projectNames: Map<string, string>;
  onViewAll: () => void;
  onSeverityClick: (sev: AlertSeverity) => void;
}) {
  const critHigh = data.bySeverity.critical + data.bySeverity.high;
  return (
    <>
      <div className="flex items-baseline gap-2">
        <button
          type="button"
          onClick={onViewAll}
          className={`text-3xl font-bold hover:underline ${critHigh > 0 ? 'text-mozaic-red' : 'text-foreground'}`}
        >
          {data.total}
        </button>
        <span className="text-sm text-muted-foreground">unack</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {ALERT_SEVERITY_ORDER.map((sev) => {
          const count = data.bySeverity[sev];
          if (count === 0) return null;
          return (
            <button
              key={sev}
              type="button"
              onClick={() => onSeverityClick(sev)}
              className="hover:opacity-80"
            >
              <SeverityChip className={ALERT_SEVERITY_STYLE[sev]}>
                {formatCategory(sev)} {count}
              </SeverityChip>
            </button>
          );
        })}
      </div>
      {data.recentUnacknowledged.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">Most recent</p>
          <ul className="mt-1 space-y-1 text-xs">
            {data.recentUnacknowledged.map((a) => (
              <li key={a.id} className="truncate">
                <span className="font-medium">{ALERT_TYPE_LABEL[a.alert_type]}</span>
                <span className="text-muted-foreground"> · </span>
                <span className="text-muted-foreground">
                  {projectNames.get(a.project_id) ?? '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Button
        variant="link"
        size="sm"
        className="mt-2 h-auto p-0 text-xs text-mozaic-teal"
        onClick={onViewAll}
      >
        View All Alerts &rarr;
      </Button>
    </>
  );
}

// ── Row 2: SSE Summary Today ────────────────────────────────

interface SSESummaryToday {
  hasManifests: boolean;
  highest: SSEByProjectRow | null;
  highSSECount: number;
  totalProjects: number;
}

function summarizeSSE(rows: SSEByProjectRow[]): SSESummaryToday {
  const withManifest = rows.filter((r) => r.hasPublishedManifest);
  if (withManifest.length === 0) {
    return {
      hasManifests: false,
      highest: null,
      highSSECount: 0,
      totalProjects: rows.length,
    };
  }
  const highest = withManifest.reduce((acc, r) =>
    r.ssePercent > acc.ssePercent ? r : acc,
  );
  const highSSECount = withManifest.filter((r) => r.ssePercent > 15).length;
  return {
    hasManifests: true,
    highest: highest.totalPOB > 0 ? highest : null,
    highSSECount,
    totalProjects: withManifest.length,
  };
}

function SSESummaryTodayCard({
  query,
}: {
  query: ReturnType<typeof useSSEToday>;
}) {
  const summary = React.useMemo(
    () => (query.data ? summarizeSSE(query.data) : null),
    [query.data],
  );
  return (
    <Card>
      <WidgetCardHeader
        title="SSE Summary Today"
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !summary ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : !summary.hasManifests ? (
          <>
            <p className="text-3xl font-bold text-muted-foreground">—</p>
            <p className="mt-1 text-xs text-muted-foreground">
              No POB manifests published today
            </p>
          </>
        ) : (
          <SSESummaryTodayContent summary={summary} />
        )}
      </CardContent>
    </Card>
  );
}

function SSESummaryTodayContent({ summary }: { summary: SSESummaryToday }) {
  const highest = summary.highest;
  const pct = highest?.ssePercent ?? 0;
  const headlineColor = pct > 15 ? 'text-mozaic-orange' : 'text-mozaic-green';
  return (
    <>
      <div className="flex items-baseline gap-2">
        <p className={`text-3xl font-bold ${headlineColor}`}>{pct}%</p>
        <span className="text-sm text-muted-foreground">highest SSE</span>
      </div>
      {highest && (
        <p className="mt-1 truncate text-xs">
          <span className="font-medium">{highest.projectName}</span>
          <span className="text-muted-foreground">
            {' '}· {highest.sseCount}/{highest.totalPOB} SSE
          </span>
        </p>
      )}
      <div className="mt-3 grid grid-cols-2 gap-x-4 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Projects {'>'} 15%</span>
          <span className={`font-medium ${summary.highSSECount > 0 ? 'text-mozaic-orange' : ''}`}>
            {summary.highSSECount}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">With manifest</span>
          <span className="font-medium">{summary.totalProjects}</span>
        </div>
      </div>
    </>
  );
}

// ── Row 2: Safety Events L7D ────────────────────────────────

function SafetyEventsCard({
  query,
  period,
  onPeriodChange,
  onTotalClick,
  onTypeClick,
}: {
  query: ReturnType<typeof useSafetyEvents>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  onTotalClick: () => void;
  onTypeClick: () => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Safety Events"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : (
          <SafetyEventsContent
            data={query.data}
            period={period}
            onTotalClick={onTotalClick}
            onTypeClick={onTypeClick}
          />
        )}
      </CardContent>
    </Card>
  );
}

// LTI is used as the fatality proxy here per S77 Gap 4 — the
// safety_event_type ENUM does not include 'fatality', so LTI is the
// closest call-out until the enum/schema is extended.
function SafetyEventsContent({
  data,
  period,
  onTotalClick,
  onTypeClick,
}: {
  data: SafetyEventsAnalysis;
  period: TimePeriod;
  onTotalClick: () => void;
  onTypeClick: () => void;
}) {
  const trend = trendIndicator(data.trend);
  const lti = data.current.lost_time_injury;
  return (
    <>
      <div className="flex items-baseline gap-2">
        <button
          type="button"
          onClick={onTotalClick}
          className={`text-3xl font-bold hover:underline ${lti > 0 ? 'text-mozaic-red' : 'text-foreground'}`}
        >
          {data.current.total}
        </button>
        <span className="text-sm text-muted-foreground">events</span>
        <span className={`ml-auto text-lg font-semibold ${trend.color}`}>{trend.symbol}</span>
      </div>
      <button
        type="button"
        onClick={onTypeClick}
        className="mt-3 grid w-full grid-cols-2 gap-x-4 gap-y-1 text-left text-xs hover:opacity-80"
      >
        <div className="flex justify-between">
          <span className="text-muted-foreground">Near Miss</span>
          <span className="font-medium">{data.current.near_miss}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">First Aid</span>
          <span className="font-medium">{data.current.first_aid}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Recordable</span>
          <span className="font-medium">{data.current.recordable_injury}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">LTI</span>
          <span className={`font-medium ${lti > 0 ? 'text-mozaic-red' : ''}`}>{lti}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Property</span>
          <span className="font-medium">{data.current.property_damage}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Environmental</span>
          <span className="font-medium">{data.current.environmental_release}</span>
        </div>
      </button>
      <p className="mt-2 text-xs text-muted-foreground">
        vs {data.previous.total} in prior {period.days} days
      </p>
    </>
  );
}

// ── Row 2: SWA Events L7D ───────────────────────────────────

function SWAEventsCard({
  query,
  period,
  onPeriodChange,
  onEventClick,
  onSeverityClick,
}: {
  query: ReturnType<typeof useSWAEvents>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  onEventClick: (dsrReportId: string) => void;
  onSeverityClick: (sev: 'critical' | 'serious' | 'moderate' | 'minor') => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="SWA Events"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : (
          <SWAEventsContent
            data={query.data}
            onEventClick={onEventClick}
            onSeverityClick={onSeverityClick}
          />
        )}
      </CardContent>
    </Card>
  );
}

function SWAEventsContent({
  data,
  onEventClick,
  onSeverityClick,
}: {
  data: SWAEventsAnalysis;
  onEventClick: (dsrReportId: string) => void;
  onSeverityClick: (sev: 'critical' | 'serious' | 'moderate' | 'minor') => void;
}) {
  const openTotal = data.openEventsByProject.reduce((sum, p) => sum + p.openCount, 0);
  const critical = data.bySeverity.critical;
  return (
    <>
      <div className="flex items-baseline gap-2">
        <p className={`text-3xl font-bold ${critical > 0 ? 'text-mozaic-red' : 'text-foreground'}`}>
          {data.total}
        </p>
        <span className="text-sm text-muted-foreground">total</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(['critical', 'serious', 'moderate', 'minor'] as const).map((sev) => {
          const count = data.bySeverity[sev];
          if (count === 0) return null;
          return (
            <button
              key={sev}
              type="button"
              onClick={() => onSeverityClick(sev)}
              className="hover:opacity-80"
            >
              <SeverityChip className={SWA_SEVERITY_STYLE[sev]}>
                {formatCategory(sev)} {count}
              </SeverityChip>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex justify-between text-xs">
        <span className="text-muted-foreground">Open / ongoing</span>
        <span className={`font-medium ${openTotal > 0 ? 'text-mozaic-orange' : ''}`}>
          {openTotal}
        </span>
      </div>
      {data.openEventsByProject.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground">Open by project</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {data.openEventsByProject.slice(0, 4).map((p) => (
              <li key={p.projectId} className="flex justify-between gap-2">
                <button
                  type="button"
                  className="truncate text-left text-mozaic-teal hover:underline"
                  onClick={() => onEventClick(p.projectId)}
                  title={p.projectName}
                >
                  {p.projectName}
                </button>
                <span className="font-medium">{p.openCount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ── Row 3: Recent DSR Activity feed ─────────────────────────

type FeedFilter =
  | 'all'
  | 'high_risk'
  | 'has_swa'
  | 'has_safety'
  | 'has_positive';

const FEED_FILTERS: { key: FeedFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'high_risk', label: 'High Risk' },
  { key: 'has_swa', label: 'Has SWA' },
  { key: 'has_safety', label: 'Has Safety Events' },
  { key: 'has_positive', label: 'Has Positive Observations' },
];

function applyFeedFilter(rows: RecentDSRRow[], f: FeedFilter): RecentDSRRow[] {
  switch (f) {
    case 'all': return rows;
    case 'high_risk':
      return rows.filter((r) => r.overallRiskLevel === 'high' || r.overallRiskLevel === 'critical');
    case 'has_swa':
      return rows.filter((r) => r.swaCount > 0);
    case 'has_safety':
      return rows.filter((r) => r.safetyEventCount > 0);
    case 'has_positive':
      return rows.filter((r) => r.positiveObservationCount > 0);
  }
}

function RecentDSRFeedCard({
  query,
  filter,
  onFilterChange,
  onLoadMore,
  canLoadMore,
  onRowClick,
}: {
  query: ReturnType<typeof useRecentDSRs>;
  filter: FeedFilter;
  onFilterChange: (f: FeedFilter) => void;
  onLoadMore: () => void;
  canLoadMore: boolean;
  onRowClick: (id: string) => void;
}) {
  const filtered = React.useMemo(
    () => (query.data ? applyFeedFilter(query.data, filter) : []),
    [query.data, filter],
  );
  return (
    <Card>
      <WidgetCardHeader
        title="Recent DSR Activity"
        size="normal"
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent className="space-y-3 p-0">
        <div className="flex flex-wrap gap-1.5 px-6 pt-1">
          {FEED_FILTERS.map((f) => {
            const active = f.key === filter;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => onFilterChange(f.key)}
                className={
                  'rounded-full border px-2.5 py-0.5 text-xs transition-colors ' +
                  (active
                    ? 'border-mozaic-teal bg-mozaic-teal/10 text-mozaic-teal'
                    : 'border-muted bg-transparent text-muted-foreground hover:bg-muted')
                }
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>SSR</TableHead>
              <TableHead>Date / Shift</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead className="text-right">Sections</TableHead>
              <TableHead>Submitted</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {query.isError && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  Could not load recent reports.
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading && !query.isError && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    {filter === 'all' ? (
                      <Inbox className="h-7 w-7 text-muted-foreground/60" />
                    ) : (
                      <ListChecks className="h-7 w-7 text-muted-foreground/60" />
                    )}
                    <p className="text-sm font-medium">
                      {filter === 'all'
                        ? 'No submitted DSRs in scope yet.'
                        : 'No DSRs match this filter.'}
                    </p>
                    {filter !== 'all' && (
                      <p className="text-xs">Try the All filter, or pick a different one.</p>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((row) => {
              const riskBadge = row.overallRiskLevel
                ? RISK_LEVEL_STYLE[row.overallRiskLevel]
                : null;
              const sectionFlags: string[] = [];
              if (row.swaCount > 0) sectionFlags.push(`${row.swaCount} SWA`);
              if (row.safetyEventCount > 0) sectionFlags.push(`${row.safetyEventCount} SE`);
              if (row.positiveObservationCount > 0) sectionFlags.push(`${row.positiveObservationCount} PO`);
              return (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onRowClick(row.id)}
                >
                  <TableCell className="font-medium">{row.projectName}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.locationName ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.ssrName ?? '—'}
                  </TableCell>
                  <TableCell>
                    <div>{format(new Date(row.reportDate), 'MMM d, yyyy')}</div>
                    <div className="text-xs capitalize text-muted-foreground">{row.shiftType}</div>
                  </TableCell>
                  <TableCell>
                    {riskBadge ? (
                      <Badge className={`capitalize ${riskBadge}`}>
                        {formatRiskLevel(row.overallRiskLevel)}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    <div className="text-muted-foreground">
                      {row.activityCount} {row.activityCount === 1 ? 'activity' : 'activities'}
                    </div>
                    {sectionFlags.length > 0 && (
                      <div className="text-mozaic-teal">{sectionFlags.join(' · ')}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.submittedAt
                      ? formatDistanceToNow(new Date(row.submittedAt), { addSuffix: true })
                      : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {canLoadMore && !query.isLoading && filtered.length > 0 && (
          <div className="flex justify-center pb-4">
            <Button variant="outline" size="sm" onClick={onLoadMore}>
              Load More
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main ────────────────────────────────────────────────────

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const { dsrId: panelDsrId, openDsr, closeDsr } = useDsrPanel();
  const tenantName = useTenantName();
  const scope = useScopeProjectIds();
  const projectIds = React.useMemo(() => scope.data ?? [], [scope.data]);
  const scopeReady = !!scope.data;

  // Per-widget time-period state (AC 12.1.10). Risk Distribution defaults
  // to Today (status), Safety + SWA default to Last 7 Days (trend).
  const [riskPeriod, setRiskPeriod] = React.useState<TimePeriod>(TIME_PERIOD_TODAY);
  const [safetyPeriod, setSafetyPeriod] = React.useState<TimePeriod>(TIME_PERIOD_7D);
  const [swaPeriod, setSwaPeriod] = React.useState<TimePeriod>(TIME_PERIOD_7D);

  const dsrsToday = useDSRsToday(projectIds, scopeReady);
  const risk = useRiskDistribution(projectIds, scopeReady, riskPeriod);
  const activeAlerts = useActiveAlerts(projectIds, scopeReady);
  const sseToday = useSSEToday(projectIds, scopeReady);
  const safety = useSafetyEvents(projectIds, scopeReady, safetyPeriod);
  const swa = useSWAEvents(projectIds, scopeReady, swaPeriod);

  const [feedFilter, setFeedFilter] = React.useState<FeedFilter>('all');
  const [feedLimit, setFeedLimit] = React.useState(20);
  const recentDSRs = useRecentDSRs(projectIds, scopeReady, feedLimit);

  const trends = useTrendAnalytics(projectIds, scopeReady);

  // Project-name lookup populated from the various scoped queries so
  // alert/SWA cards can resolve project_id → name without a dedicated
  // service call.
  const projectNames = React.useMemo(() => {
    const m = new Map<string, string>();
    (sseToday.data ?? []).forEach((p) => m.set(p.projectId, p.projectName));
    (recentDSRs.data ?? []).forEach((r) => m.set(r.projectId, r.projectName));
    (swa.data?.openEventsByProject ?? []).forEach((p) => m.set(p.projectId, p.projectName));
    (safety.data?.projectsWithLTI ?? []).forEach((p) => m.set(p.projectId, p.projectName));
    return m;
  }, [sseToday.data, recentDSRs.data, swa.data, safety.data]);

  const dsrTrendState = useChartState(trends.dsrTrend, 'No submitted DSRs in this period');
  const dsrTrendEmpty =
    !trends.dsrTrend.isLoading &&
    !trends.dsrTrend.isError &&
    (trends.dsrTrend.data ?? []).every((p) => p.dsr_count === 0);
  const safetyTrendState = useChartState(trends.safetyTrend, 'No safety events recorded');
  const safetyTrendEmpty =
    !trends.safetyTrend.isLoading &&
    !trends.safetyTrend.isError &&
    (trends.safetyTrend.data ?? []).every(
      (p) =>
        p.near_miss_count +
          p.first_aid_count +
          p.recordable_count +
          p.lti_count +
          p.good_catch_count ===
        0,
    );
  const sseTrendHasData = (trends.sseTrend.data ?? []).length > 0;

  const canLoadMore =
    !!recentDSRs.data && recentDSRs.data.length === feedLimit;

  return (
    <div className="space-y-6">
      {/* Scope context bar */}
      <div className="rounded-md bg-mozaic-navy/5 px-4 py-2 text-sm">
        Viewing:{' '}
        <span className="font-medium">{tenantName.data ?? 'Your Scope'}</span>
        {scope.data && (
          <span className="text-muted-foreground">
            {' '}— {projectIds.length} project{projectIds.length === 1 ? '' : 's'} in scope
          </span>
        )}
      </div>

      {/* Row 1 — Today's Status */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <ActiveDSRsTodayCard
          query={dsrsToday}
          onProjectClick={(projectId) => navigate(`/reports?project=${projectId}`)}
        />
        <RiskDistributionCard
          query={risk}
          period={riskPeriod}
          onPeriodChange={setRiskPeriod}
          hasHigh={dsrsToday.data?.hasHigh ?? false}
          onRiskClick={(level) =>
            navigate(level === 'not_assessed' ? '/reports' : `/reports?risk=${level}`)
          }
        />
        <ActiveAlertsCard
          query={activeAlerts}
          projectNames={projectNames}
          onViewAll={() => navigate('/alerts')}
          onSeverityClick={(sev) => navigate(`/alerts?severity=${sev}`)}
        />
      </div>

      {/* Row 2 — Workforce and Safety Events */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <SSESummaryTodayCard query={sseToday} />
        <SafetyEventsCard
          query={safety}
          period={safetyPeriod}
          onPeriodChange={setSafetyPeriod}
          onTotalClick={() => navigate('/reports')}
          onTypeClick={() => navigate('/reports')}
        />
        <SWAEventsCard
          query={swa}
          period={swaPeriod}
          onPeriodChange={setSwaPeriod}
          onEventClick={(projectId) => navigate(`/reports?project=${projectId}`)}
          onSeverityClick={(sev) => navigate(`/reports?swa_severity=${sev}`)}
        />
      </div>

      {/* Row 3 — Recent DSR Activity */}
      <RecentDSRFeedCard
        query={recentDSRs}
        filter={feedFilter}
        onFilterChange={setFeedFilter}
        onLoadMore={() => setFeedLimit((n) => n + 20)}
        canLoadMore={canLoadMore}
        onRowClick={(id) => id && openDsr(id)}
      />

      {/* Preserved: Trend charts (S58/S59 carry-over) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Daily Risk Level — Last 30 Days"
          {...dsrTrendState}
          isEmpty={dsrTrendEmpty}
        >
          <DailyRiskChart data={trends.dsrTrend.data ?? []} />
        </ChartCard>
        <ChartCard
          title="Safety Events — Last 30 Days"
          {...safetyTrendState}
          isEmpty={safetyTrendEmpty}
        >
          <SafetyEventLineChart data={trends.safetyTrend.data ?? []} />
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {(sseTrendHasData || trends.sseTrend.isLoading) && (
          <ChartCard
            title="SSE % — Last 30 Days"
            isLoading={trends.sseTrend.isLoading}
            isError={trends.sseTrend.isError}
            isEmpty={!sseTrendHasData && !trends.sseTrend.isLoading}
            emptyMessage="No POB manifests in this period"
            onRetry={trends.sseTrend.refetch}
          >
            <SSEAreaChart data={trends.sseTrend.data ?? []} />
          </ChartCard>
        )}
        <TopActionItemsCard
          items={trends.actions.data}
          isLoading={trends.actions.isLoading}
          isError={trends.actions.isError}
          onViewAll={() => navigate('/reports')}
        />
      </div>

      <DSRDetailPanel dsrId={panelDsrId} onClose={closeDsr} />
    </div>
  );
}
